// Slice-10 auth context.
//
// Mediates the slice-4 auth flow between the SPA and the API:
//   • Login: callbacks store the access token in memory + cache the
//     MeResponse so the rest of the app can read identity claims.
//   • Logout: clears in-memory state + posts /auth/logout so the
//     refresh family is revoked.
//   • Boot: on app mount the provider attempts a /auth/refresh to
//     recover a session from the HttpOnly refresh cookie. This is the
//     pattern that survives a hard page refresh without persisting
//     access tokens to localStorage (security trade-off ratified in
//     Plan §7.2).

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { apiClient, setAccessToken } from "../api/client";

/**
 * Authenticated user metadata sourced from the /auth/me response.
 *
 * **Trust boundary** (Slice F v2 / ADR 0006v2): `uiHints` is a list of
 * permission strings the server says the user is *probably* allowed to
 * exercise. It is for UI rendering only — show/hide controls, drive
 * empty-state copy, etc. Never trust it for authorization. Every
 * protected API endpoint enforces authorization server-side via
 * `IRollCallAuthorizationService`; a non-admin caller who manages to
 * invoke a mutation will receive 401 / 403 regardless of what the UI
 * displays. If you find yourself reaching for `uiHints` in a place that
 * looks like a security gate, the right answer is almost certainly to
 * let the API call go through and let the 403 surface the message.
 */
export interface AuthUser {
  userId: string;
  displayName: string;
  email: string;
  uiHints: readonly string[];
  tenant: string;
}

export interface AuthState {
  status: "loading" | "anonymous" | "authenticated";
  user: AuthUser | null;
  /** Latest minted access token. Slice-12 closeout will replace with /me echo. */
  accessToken: string | null;
}

interface AuthContextValue extends AuthState {
  loginWithTokenBundle: (bundle: TokenBundle, me: AuthUser) => void;
  logout: () => Promise<void>;
}

interface TokenBundle {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<AuthState>({
    status: "loading",
    user: null,
    accessToken: null,
  });

  const loginWithTokenBundle = useCallback((bundle: TokenBundle, me: AuthUser) => {
    setAccessToken(bundle.accessToken);
    setState({
      status: "authenticated",
      user: me,
      accessToken: bundle.accessToken,
    });
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiClient.POST("/auth/logout", {});
    } catch {
      // Best-effort; even if the network call fails we still want to
      // wipe local state so the UI returns to the login screen.
    }
    setAccessToken(null);
    setState({ status: "anonymous", user: null, accessToken: null });
  }, []);

  // Boot-time refresh: try to recover a session from the HttpOnly
  // refresh cookie. If it succeeds, fetch /auth/me to populate the
  // user; if it fails, the SPA renders the login screen.
  //
  // ORC-29 / Slice 3 — Playwright bootstrap shim. The suite seeds a real
  // RollCall access token into window.__BOOTSTRAP_ACCESS_TOKEN__ via
  // addInitScript() so the route guard finds a token synchronously
  // (before this useEffect runs). We pick it up here, skip the refresh
  // round-trip (the test mint endpoint already issued one), and go
  // straight to /auth/me.
  //
  // No `import.meta.env.DEV` gate (cf. web/src/api/client.ts comment):
  // the suite runs against the `vite preview` production build, where
  // DEV is `false`. When the variable is absent in normal production
  // operation the read is a no-op.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const bootstrapToken =
          typeof window !== "undefined"
            ? (window as unknown as { __BOOTSTRAP_ACCESS_TOKEN__?: string }).__BOOTSTRAP_ACCESS_TOKEN__
            : undefined;

        let accessTokenValue: string | null;
        if (bootstrapToken) {
          accessTokenValue = bootstrapToken;
          setAccessToken(bootstrapToken);
        } else {
          const refresh = await apiClient.POST("/auth/refresh", {});
          if (!refresh.data || cancelled) {
            if (!cancelled) {
              setState({ status: "anonymous", user: null, accessToken: null });
            }
            return;
          }
          accessTokenValue = refresh.data.access_token;
          setAccessToken(refresh.data.access_token);
        }
        const me = await apiClient.GET("/auth/me", {});
        if (cancelled) {
          return;
        }
        if (me.data) {
          setState({
            status: "authenticated",
            user: {
              userId: me.data.user_id,
              displayName: me.data.display_name,
              email: me.data.email,
              // Slice F (v2): `permissions` renamed to `ui_hints` per ADR 0006v2.
              // UI hints only — never trust for authorization. See AuthUser docstring.
              uiHints: me.data.ui_hints ?? [],
              // /auth/me doesn't echo tenant; the SPA already knows it
              // from the discovery step or via the JWT payload. Slice
              // 11 SPA pass parses the token if needed; for slice 10
              // we leave it empty and the AppShell only reads it for
              // future use.
              tenant: "",
            },
            accessToken: accessTokenValue,
          });
        } else {
          setAccessToken(null);
          setState({ status: "anonymous", user: null, accessToken: null });
        }
      } catch {
        if (!cancelled) {
          setState({ status: "anonymous", user: null, accessToken: null });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, loginWithTokenBundle, logout }),
    [state, loginWithTokenBundle, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (value === null) {
    throw new Error("useAuth must be called inside <AuthProvider>");
  }
  return value;
}

/** Hook that tests use to inject pre-baked auth state without hitting the API. */
export function createAuthContextValueForTesting(state: AuthState): AuthContextValue {
  return {
    ...state,
    loginWithTokenBundle: () => undefined,
    logout: async () => undefined,
  };
}

export { AuthContext };
